import * as React from 'react';
import {useRef, useEffect, useContext, useMemo} from 'react';
import type {Merge, NodeAnimation} from '@sail/engine';
import {
  AnimatePresence,
  animate,
  createViewConfig,
  view,
  media,
  useIsomorphicLayoutEffect,
} from '@sail/engine';
import {cancel} from '@sail/icons/react/Icon';
import type {MutableRefObject} from 'react';
import {tokens as $} from '@sail/tokens';
import {useIntl} from '@sail/intl';
import {useId} from '@sail/react-aria';
import type {View} from '../../view';
import {Drawer} from './Drawer';
import type {BaseDrawerProps} from './BaseDrawer';
import {DrawerContext, useShouldScroll} from './BaseDrawer';
import type {DrawerProps} from './DrawerProps';
import {Button, ButtonConfig} from '../Button';
import {Action} from '../Action';
import {Icon} from '../Icon';
import {actionButton} from '../../utilStyles/ActionButton';
import {css} from '../../css';
import {useMediaQuery} from '../../providers/UiMediaQueryProvider';
import {
  closeButtonLabel,
  closePreviewButtonLabel,
  showPreviewButtonLabel,
  stepProgressLabel,
  bottomSheetPreviewTabTitle,
  bottomSheetEditTabTitle,
} from './messages';
import {useShouldDisplayBottomSheet} from '../bottom-sheet/useShouldDisplayBottomSheet';
import {useRegisterCreationDrawerHeaderInChildren} from './useCreationDrawerHeaderInChildren';
import {isComponentType} from '../../lib/isComponentType';

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export type CreationDrawerHeaderProps = {
  /** The title of the drawer */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** If the stepText is provided, do not render the default step counter */
  stepText?: React.ReactNode;
  /** The function to be called when the user attempts to toggle the preview pane open/close. If not provided, a button to show/hide the preview area will not be rendered. */
  onTogglePreview?: () => void | undefined;
  /** Whether or not the preview pane is currently shown */
  showPreview?: boolean | undefined;
  /** The step in the flow that the user is currently on */
  currentStep?: number;
  /** The total number of steps in the flow */
  totalSteps?: number;
  actions?: React.ReactNode;
  /** Text to render on the Close preview button, defaults to "Close preview" */
  closePreviewText?: string;
  /** Text to render on the Show preview button, defaults to "Show preview" */
  showPreviewText?: string;
};

const drawerStepsText = (
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  stepText?: React.ReactNode,
  currentStep?: number,
  totalSteps?: number,
): React.ReactNode => {
  let stepMessage = null;

  if (stepText) {
    stepMessage = stepText;
  } else if (currentStep && totalSteps) {
    stepMessage = `• ${formatMessage(stepProgressLabel, {
      step: currentStep,
      totalSteps,
    })}`;
  }

  if (!stepMessage) {
    return null;
  }

  return <view.span css={{color: 'subdued'}}>{stepMessage}</view.span>;
};

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawerHeaderConfig = createViewConfig({
  props: {} as CreationDrawerHeaderProps,
  name: 'CreationDrawerHeader',
  flattens: true,
});

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawerHeader = CreationDrawerHeaderConfig.createView(
  ({
    title,
    subtitle,
    stepText,
    showPreview,
    actions,
    onTogglePreview,
    currentStep,
    totalSteps,
    closePreviewText,
    showPreviewText,
    ...props
  }) => {
    const labelId = useId();
    const descriptionId = useId();

    useRegisterCreationDrawerHeaderInChildren();

    const drawerContext = useContext(DrawerContext);
    const {
      onClose,
      registerLabelId,
      registerDescriptionId,
      bottomSheetVariant,
    } = drawerContext ?? {};

    const {formatMessage} = useIntl();
    const resolvedClosePreviewText =
      closePreviewText ?? formatMessage(closePreviewButtonLabel);
    const resolvedShowPreviewText =
      showPreviewText ?? formatMessage(showPreviewButtonLabel);
    const shouldDisplayBottomSheet = useShouldDisplayBottomSheet('drawer');

    const isFullscreenSheetOrDesktop =
      !bottomSheetVariant || bottomSheetVariant === 'fullscreen-sheet';

    useIsomorphicLayoutEffect(() => {
      registerLabelId?.(title ? labelId : undefined);
      registerDescriptionId?.(subtitle ? descriptionId : undefined);
      // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
    }, [title, subtitle, labelId, descriptionId]);

    return (
      <view.div
        {...props}
        css={[
          css({
            paddingX: 'space.250',
            stack: 'x',
            gap: 'large',
          }),
          title ? css({paddingY: 'medium'}) : css({paddingTop: 'space.150'}),
          title && bottomSheetVariant
            ? css({paddingBottom: $['space.150']})
            : css({}),
          title && !bottomSheetVariant
            ? css({borderBottom: `1px solid ${$['color.border']}`})
            : css({}),
          title
            ? css({distribute: 'space-between', alignX: 'start'})
            : css({distribute: 'packed', alignX: 'end'}),
        ]}
      >
        {title ? (
          <view.div css={{stack: 'y', gap: 'xxsmall'}}>
            {subtitle ? (
              <view.div
                css={{
                  textColor: 'subdued',
                  [media.up($['viewport.mobile'])]: {
                    font: 'body.small',
                  },
                  [media.down($['viewport.mobile'])]: {
                    font: 'label.medium',
                  },
                }}
              >
                <view.span
                  id={descriptionId}
                  css={{
                    [media.up($['viewport.mobile'])]: {
                      fontWeight: 'semibold',
                    },
                    [media.down($['viewport.mobile'])]: {
                      font: 'label.medium.emphasized',
                    },
                  }}
                >
                  {subtitle}
                </view.span>{' '}
                {drawerStepsText(
                  formatMessage,
                  stepText,
                  currentStep,
                  totalSteps,
                )}
              </view.div>
            ) : null}
            <view.h1
              id={labelId}
              css={[
                css({margin: 'space.0'}),
                isFullscreenSheetOrDesktop
                  ? css({font: 'heading.medium'})
                  : css({font: 'heading.small'}),
              ]}
            >
              {title}
            </view.h1>
          </view.div>
        ) : null}

        <view.div
          css={{
            stack: 'x',
            gap: 'space.150',
            width: 'fit',
            alignY: 'center',
          }}
        >
          {actions}
          {onTogglePreview && !shouldDisplayBottomSheet ? (
            <Button onPress={onTogglePreview}>
              {showPreview ? resolvedClosePreviewText : resolvedShowPreviewText}
            </Button>
          ) : null}
          <Action
            aria-label={formatMessage(closeButtonLabel)}
            onPress={onClose}
            uses={actionButton}
            css={{bleedRight: 'small'}}
          >
            <Icon icon={cancel} />
          </Action>
        </view.div>
      </view.div>
    );
  },
);

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export type CreationDrawerFooterProps = View.IntrinsicElement<'div'> & {
  /** The contents that appear on the left side of the drawer footer */
  children?: React.ReactNode;
  /** The right-most action in the footer, used to confirm or move forward in the flow */
  primaryAction: React.ReactNode;
  /** Any actions other than the primary action */
  secondaryAction?: React.ReactNode;
};

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawerFooterConfig = createViewConfig({
  props: {} as CreationDrawerFooterProps,
  name: 'CreationDrawerFooter',
  flattens: true,
});

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawerFooter = CreationDrawerFooterConfig.createView(
  ({children, secondaryAction, primaryAction, ...props}) => {
    const {isSmallScreen} = useMediaQuery();
    const drawerContext = useContext(DrawerContext);
    const {bottomSheetVariant} = drawerContext ?? {};

    return (
      <view.div
        {...props}
        css={
          !bottomSheetVariant
            ? css({borderBlockStart: `1px solid ${$['color.border']}`})
            : css({})
        }
      >
        <view.div
          css={{
            color: 'subdued',
            [media.up($['viewport.mobile'])]: {
              font: 'body.small',
            },
            [media.down($['viewport.mobile'])]: {
              font: 'body.medium',
            },
          }}
        >
          {children}
        </view.div>
        <view.div
          css={{
            stack: 'x',
            wrap: 'wrap',
            display: 'inline',
            [media.up($['viewport.mobile'])]: {
              gap: 'small',
            },
            [media.down($['viewport.mobile'])]: {
              gap: 'medium',
              width: 'fill',
            },
          }}
          uses={[
            ButtonConfig.customize({
              defaults: {size: isSmallScreen ? 'large' : 'medium'},
              css: {
                [media.down($['viewport.mobile'])]: {
                  flex: '1 1 0',
                },
              },
            }),
          ]}
        >
          {secondaryAction}
          {primaryAction}
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      padding: 'space.250',
      alignY: 'center',
      [media.up($['viewport.mobile'])]: {
        stack: 'x',
      },
      [media.down($['viewport.mobile'])]: {
        stack: 'y',
      },
    },
  },
);

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export type CreationDrawerProps = Merge<
  DrawerProps,
  {
    /** The title of the drawer */
    title?: React.ReactNode;
    /** The function to be called when the user attempts to toggle the preview pane open/close. If not provided, a button to show/hide the preview area will not be rendered. */
    onTogglePreview?: () => void | undefined;
    /** Whether or not the preview pane is currently shown */
    showPreview?: boolean | undefined;
    /** The function to be called when the user attempts to close the drawer */
    onClose: () => void;
    preview?: React.ReactNode;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    subviews?: BaseDrawerProps['subviews'] & View.Subviews<{preview: 'div'}>;
  }
>;

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawerConfig = createViewConfig({
  props: {} as CreationDrawerProps,
  name: 'CreationDrawer',
  flattens: true,
});

const CreationDrawerRender = ({
  title,
  showPreview,
  onTogglePreview,
  header,
  footer,
  preview,
  children,
  onClose,
  subviews,
  ...props
}: CreationDrawerProps): JSX.Element => {
  const {formatMessage} = useIntl();
  const shouldDisplayBottomSheet = useShouldDisplayBottomSheet('drawer');
  const {preview: previewSubview, ...baseDrawerSubviews} = subviews!;
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewAnimationRef = useRef<NodeAnimation | null>(null);
  const shouldScroll = useShouldScroll(props.size);

  const prevShowPreview = useRef<boolean | undefined>(showPreview);

  useEffect(() => {
    prevShowPreview.current = showPreview;
  });

  // This performs a layout animation. We don't natively support that yet, so
  // this is low-level animation code. In the future this will be natively
  // supported.
  useIsomorphicLayoutEffect(() => {
    const preview = previewRef.current;
    const drawer = drawerRef.current;
    const previewAnimation = previewAnimationRef.current;

    if (preview && drawer && previewAnimation) {
      // eslint-disable-next-line @sail/ui-no-reflow
      const rect = preview.getBoundingClientRect();
      if (prevShowPreview.current === false && showPreview) {
        if (!shouldScroll) {
          // on desktop, move the drawer to the left by the amount of width of the preview
          drawer.style.transform = `translateX(${rect.width}px)`;
          previewAnimation.play();
        }
      }

      if (
        prevShowPreview.current === true &&
        showPreview === false &&
        previewAnimation &&
        !shouldScroll
      ) {
        // this is playing the animation in reverse but going back to a new value
        const task = previewAnimation.play({
          reverse: 'effects',
          backTo: {
            transform: `translateX(${rect.width}px)`,
          },
        });

        const waitForPreviewRemoval = new Promise<void>((resolve) => {
          const check = () => {
            if (!previewRef.current?.isConnected) {
              resolve();
            } else {
              requestAnimationFrame(check);
            }
          };
          check();
        });

        Promise.all([task.getFinished(), waitForPreviewRemoval]).then(() => {
          if (drawerRef.current) {
            drawerRef.current.style.transform = 'translateX(0px)';
          }
        });
      }
    }
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, [showPreview]);

  const hasOnTogglePreview = useMemo(() => {
    if (isComponentType(header, CreationDrawerHeader)) {
      return header.props.onTogglePreview !== undefined;
    }

    return onTogglePreview !== undefined;
  }, [onTogglePreview, header]);

  return (
    <Drawer
      onClose={onClose}
      {...props}
      subviews={baseDrawerSubviews}
      ref={drawerRef}
      uses={[
        animate
          .transition({transform: 'translateX(0px)', duration: 200})
          .ref(previewAnimationRef as MutableRefObject<NodeAnimation>),
      ]}
      header={
        header || (
          <CreationDrawerHeader
            title={title}
            showPreview={showPreview}
            onTogglePreview={onTogglePreview}
          />
        )
      }
      end={
        <AnimatePresence>
          {/* On bottom sheets, rendering `end` content is what creates the Preview tab. When `onTogglePreview` is set, we always render it regardless of `showPreview` — tab selection controls visibility instead. */}
          {showPreview || (shouldDisplayBottomSheet && hasOnTogglePreview) ? (
            <view.div
              css={{
                stack: 'y',
                overflow: 'hidden',
              }}
              uses={[
                shouldScroll ? css({flexBasis: 'auto'}) : null,
                animate.in({
                  transform: 'translateX(100%)',
                  disabled: shouldDisplayBottomSheet,
                  duration:
                    prevShowPreview.current === true && showPreview === true
                      ? 0 // Don't animate if preview is open on initial render
                      : 200,
                }),
                animate.out({
                  transform: 'translateX(100%)',
                  disabled: shouldDisplayBottomSheet,
                  duration: 200,
                }),
              ]}
            >
              <view.div
                css={[
                  css({
                    stack: 'y',
                    overflow: 'auto',
                    padding: 'space.250',
                    flexShrink: 1,
                  }),
                  shouldDisplayBottomSheet
                    ? css({})
                    : css({
                        borderLeft: `1px solid ${$['color.border']}`,
                      }),
                ]}
                ref={previewRef}
                inherits={previewSubview}
              >
                {preview}
              </view.div>
            </view.div>
          ) : null}
        </AnimatePresence>
      }
      bottomSheetEndTabTitle={formatMessage(bottomSheetPreviewTabTitle)}
      bottomSheetChildrenTabTitle={formatMessage(bottomSheetEditTabTitle)}
      footer={footer}
    >
      <view.div
        css={[
          css({
            stack: 'y',
            padding: 'space.250',
          }),
          shouldDisplayBottomSheet
            ? css({})
            : css({
                overflow: 'auto',
                position: 'relative',
                height: 'fill',
              }),
        ]}
      >
        {children}
      </view.div>
    </Drawer>
  );
};

/**
 * @deprecated Use `Dialog` or `FullScreenOverlay` instead.
 */
export const CreationDrawer = CreationDrawerConfig.createView(
  CreationDrawerRender,
  {
    subviews: {
      header: {
        css: {
          width: 'fill',
        },
      },
    },
  },
);
